import React, { createRef, useEffect, useState } from 'react';
import cn from 'classnames';

import LoginForm from '../../../components/common/forms/LoginForm';
import Button from '../../../components/common/ui/Button';
import Input from '../../../components/form/Input';
import RoundCountdown from '../../../components/common/features/RoundCountdown';
import { RESEND_CODE_COUNTER, CODE_EXPIRATION_TIME } from '../../../constants';
import LineTextCountdown from '../../../components/common/features/LineTextCountdown';
import { MOUSEFLOW_MASKED } from '../../../constants/mouseflowClasses';

import styles from './styles.module.scss';

const CodeVerificationForm = ({
  formHandler,
  error,
  resendHandler,
  isLoading,
  backToEmail,
}) => {
  const verificationCodeRefs = Array.from({ length: 4 }, () => createRef());
  const [verificationCode, setVerificationCode] = useState('');
  const [endDate, setEndDate] = useState(
    Date.now() + RESEND_CODE_COUNTER * 1000,
  );
  const [codeExpirationDate, setCodeExpirationDate] = useState(
    Date.now() + CODE_EXPIRATION_TIME * 1000,
  );
  const [secondsRemain, setSecondsRemain] = useState(RESEND_CODE_COUNTER);
  const [showCountdown, setShowCountdown] = useState(true);
  const [disabled, setDisabled] = useState(true);

  const submitForm = () => {
    if (verificationCode.length === 4) formHandler(verificationCode);
  };

  const handleInputKeyUp = (index, event) => {
    const value = event.target.value;
    const isBackSpace =
      event.nativeEvent.code === 'Backspace' ||
      event.nativeEvent.key === 'Backspace' ||
      event.keyCode === 8;
    if (event.nativeEvent.code === 'ArrowRight' && index < 5) {
      verificationCodeRefs[index + 1]?.current?.focus();
    } else if (event.nativeEvent.code === 'ArrowLeft') {
      verificationCodeRefs[index - 1]?.current?.focus();
    } else if (
      value >= 0 &&
      /^[0-9]$/.test(value) &&
      index < 5 &&
      event.nativeEvent.code !== 'Control'
    ) {
      verificationCodeRefs[index + 1]?.current?.focus();
    } else if (isBackSpace && index > 0) {
      verificationCodeRefs[index].current.value = '';
      verificationCodeRefs[index - 1]?.current?.focus();
    } else {
      verificationCodeRefs[index].current.value = '';
    }
    updateVerificationCode();
  };

  const handlePaste = (event) => {
    const pastedCode = event.clipboardData.getData('text/plain').trim();
    const codeArray = pastedCode.split('').slice(0, 4);

    codeArray.forEach((char, index) => {
      if (/^[0-9]$/.test(char)) {
        verificationCodeRefs[index].current.value = char;
      }
    });
    updateVerificationCode();
  };

  const handleFocus = (index) => {
    verificationCodeRefs[index]?.current?.select();
  };

  const updateVerificationCode = () => {
    const verificationCodeArray = verificationCodeRefs.map(
      (ref) => ref?.current?.value || '',
    );
    const joinedCode = verificationCodeArray.join('');
    setVerificationCode(joinedCode);
  };

  const clearVerificationCode = () => {
    setVerificationCode('');
    verificationCodeRefs.forEach((ref) => (ref.current.value = null));
  };

  const updateCountdown = () => {
    const intervalId = setInterval(() => {
      const currentTime = Date.now();
      const timeDifference = endDate - currentTime;
      let secondsRemaining = Math.floor(timeDifference / 1000);
      if (secondsRemaining > 0) {
        setSecondsRemain(secondsRemaining);
      } else {
        setShowCountdown(false);
        clearInterval(intervalId);
      }
    }, 1000);
  };

  const handleResendCode = async () => {
    clearVerificationCode();
    await resendHandler();
    setSecondsRemain(RESEND_CODE_COUNTER);
    setEndDate(Date.now() + RESEND_CODE_COUNTER * 1000);
    setCodeExpirationDate(Date.now() + CODE_EXPIRATION_TIME * 1000);
    setShowCountdown(true);
  };

  useEffect(() => {
    updateCountdown();
    // eslint-disable-next-line
  }, [endDate]);

  useEffect(() => {
    submitForm();
    setDisabled(showCountdown || isLoading);
    // eslint-disable-next-line
  }, [endDate, verificationCode, showCountdown, isLoading]);

  return (
    <LoginForm
      title={'Code'}
      description={
        'Enter the 4-digit code we sent to your email or phone, if your account is registered.'
      }
      type={'codeVerification'}
    >
      <div className={styles.codeVerificationWrapper}>
        <fieldset className={styles.codeVerification}>
          {verificationCodeRefs.map((ref, index) => (
            <Input
              className={cn(MOUSEFLOW_MASKED, styles.codeVerificationInput)}
              key={index}
              type="number"
              name={`code-input-${index}`}
              maxLength="1"
              placeholder={0}
              onPaste={(e) => handlePaste(e)}
              onFocus={() => handleFocus(index)}
              onKeyUp={(e) => handleInputKeyUp(index, e)}
              innerRef={ref}
              error={!!error}
            />
          ))}
        </fieldset>
        {error ? (
          <p className={cn('text-error', styles.codeVerificationMessage)}>
            {error}
          </p>
        ) : (
          <p
            className={cn(
              styles.codeVerificationCountdown,
              styles.codeVerificationMessage,
            )}
          >
            <LineTextCountdown
              message={'Code is valid for'}
              textExpiration={'Your code has expired'}
              codeExpirationDate={codeExpirationDate}
              key={codeExpirationDate}
            ></LineTextCountdown>
          </p>
        )}
      </div>
      <Button
        type={'button'}
        className={styles.codeVerificationButton}
        disabled={disabled}
        colorType={'grey'}
        onClick={handleResendCode}
        isLoading={isLoading}
      >
        <span>Send code again</span>
        {showCountdown && (
          <RoundCountdown
            secondsRemain={secondsRemain}
            endDate={endDate}
            seconds={RESEND_CODE_COUNTER}
          ></RoundCountdown>
        )}
      </Button>
      <div className={styles.codeVerificationBackToEmailWrapper}>
        <button
          type="button"
          onClick={backToEmail}
          className={styles.codeVerificationBackToEmail}
        >
          Change Email
        </button>
      </div>
    </LoginForm>
  );
};
export default CodeVerificationForm;
